/* eslint-disable react/require-default-props */
import React, {useEffect, useState} from 'react';
import {KeyboardTypeOptions, View} from 'react-native';

import Checkbox from '/component/base/Checkbox/Checkbox';
import Text from '/component/base/Text';
import TextInput from '/component/base/TextInput/TextInput';
import {Shared} from '/constant/TestIDs';
import MyTeamsTestIDs from '/constant/TestIDs/MyTeams';
import testProperties from '/util/testing/testProperties';

import RadioButton from './RadioButton';
import {styles} from './RadioButton.style';

interface Option {
  text: string;
  id: string;
  selected: boolean;
  defaultVal: boolean;
}

interface FilterRangeDetails {
  options?: Option[];
  isFilterEnabled?: boolean;
  minDigit: number;
  maxDigit: number;
  resultRangeState: string; //above / below / between
  filterByText: string;
  aboveValue: string;
  belowValue: string;
  isError: boolean;
  ValidationError?: boolean;
  keyboardType: KeyboardTypeOptions;
  isFloatAllowed: boolean;
  isNegativeNumAllowed: boolean;
}

interface FilterRangeInputRadioGroup {
  Title: string;
  FilterRangeDetails: FilterRangeDetails;
  setFilterRangeDetails: any;
  resetTrigger?: number;
  validateTrigger?: number;
  onValidationComplete?: (hasError: boolean) => void;
  isBold?: boolean;
}

const RadioButtonGroup: React.FC<FilterRangeInputRadioGroup> = ({
  Title = '',
  FilterRangeDetails,
  setFilterRangeDetails,
  resetTrigger,
  validateTrigger,
  onValidationComplete,
}) => {
  const {
    minDigit,
    maxDigit,
    resultRangeState,
    filterByText,
    aboveValue,
    belowValue,
    options,
    isFilterEnabled = false,
    keyboardType,
    isFloatAllowed,
    isNegativeNumAllowed,
  } = FilterRangeDetails;

  const InitialRangeOptions = [
    {
      text: 'Above',
      id: 'above',
      selected: resultRangeState === 'above',
      defaultVal: true,
    },
    {
      text: 'Below',
      id: 'below',
      selected: resultRangeState === 'below',
      defaultVal: false,
    },
    {
      text: 'Between',
      id: 'between',
      selected: resultRangeState === 'between',
      defaultVal: false,
    },
  ];
  const InitialErrorState = {
    rangeValue: '',
    minValue: '',
    maxValue: '',
  };

  const twoDecimalRegex = /^-?\d*(\.\d{0,2})?$/;
  const [RangeOptions, setRangeOptions] = useState(InitialRangeOptions);
  const [errors, setErrors] = useState(InitialErrorState);
  const selectedRangeId = RangeOptions.find(opt => opt.selected)?.id;

  // Reset all internal states when resetTrigger changes
  useEffect(() => {
    if (resetTrigger !== undefined) {
      setRangeOptions(InitialRangeOptions);
      setErrors(InitialErrorState);
      // Also reset the parent state to initial values
      setFilterRangeDetails({
        ...FilterRangeDetails,
        isFilterEnabled: false,
        resultRangeState: 'above',
        aboveValue: '',
        belowValue: '',
        isError: true,
        ValidationError: true,
        options:
          FilterRangeDetails.options?.map(opt => ({
            ...opt,
            selected: opt.defaultVal || false,
          })) || [],
      });
    }
  }, [resetTrigger, setFilterRangeDetails]);

  // Trigger validation when validateTrigger changes
  useEffect(() => {
    if (validateTrigger !== undefined && validateTrigger > 0) {
      let hasValidationError = false;
      let newErrors = {
        rangeValue: '',
        minValue: '',
        maxValue: '',
      };

      // If filter is not enabled, no validation error
      if (!isFilterEnabled) {
        hasValidationError = false;
      } else {
        // Check validation based on selected range type
        const currentSelectedRangeId = RangeOptions.find(
          opt => opt.selected,
        )?.id;

        if (currentSelectedRangeId === 'above') {
          const value = aboveValue.trim();
          if (
            value === '' ||
            value === '-' ||
            value === '.' ||
            !IsValidNumber(value)
          ) {
            hasValidationError = true;
            newErrors.rangeValue = 'Please enter valid value';
          }
        } else if (currentSelectedRangeId === 'below') {
          const value = belowValue.trim();
          if (
            value === '' ||
            value === '-' ||
            value === '.' ||
            !IsValidNumber(value)
          ) {
            hasValidationError = true;
            newErrors.rangeValue = 'Please enter valid value';
          }
        } else if (currentSelectedRangeId === 'between') {
          const minValue = aboveValue.trim();
          const maxValue = belowValue.trim();

          // Check min value
          if (
            minValue === '' ||
            minValue === '-' ||
            minValue === '.' ||
            !IsValidNumber(minValue)
          ) {
            hasValidationError = true;
            newErrors.minValue = 'Enter minimum value';
          }
          // Check max value
          if (
            maxValue === '' ||
            maxValue === '-' ||
            maxValue === '.' ||
            !IsValidNumber(maxValue)
          ) {
            hasValidationError = true;
            newErrors.maxValue = 'Enter maximum value';
          }
          // Check cross-validation: min < max
          if (!hasValidationError && minValue !== '' && maxValue !== '') {
            const minNum = Number(minValue);
            const maxNum = Number(maxValue);
            if (minNum >= maxNum) {
              hasValidationError = true;
              newErrors.minValue = 'Min must be less than max';
              newErrors.maxValue = 'Max must be greater than min';
            }
          }
        }
      }

      // Update internal errors state
      setErrors(newErrors);

      // Update parent state with ValidationError
      setFilterRangeDetails({
        ...FilterRangeDetails,
        ValidationError: hasValidationError,
      });

      // Call the validation complete callback if provided
      if (onValidationComplete) {
        onValidationComplete(hasValidationError);
      }
    }
  }, [validateTrigger]);

  const OnChange = (index: number) => {
    const updatedOptions = options?.map((opt, i) => ({
      ...opt,
      selected: i === index,
    }));
    setFilterRangeDetails({
      ...FilterRangeDetails,
      options: updatedOptions,
      resultRangeState: 'above',
      aboveValue: '',
      belowValue: '',
      isError: true,
      ValidationError: true,
    });
    setRangeOptions(
      RangeOptions.map((opt, i) => ({
        ...opt,
        selected: i === 0,
      })),
    );
    setErrors(InitialErrorState);
  };

  function IsValidNumber(input: string) {
    if (input === null || input === undefined) return false;

    const originalStr = String(input);
    if (originalStr !== originalStr.trim()) return false;

    const str = originalStr.trim();

    // allow typing states (but not starting with . to prevent .5 type inputs)
    if (str === '' || str === '-' || str === '-.') return true;

    // Check for negative sign allowance
    const isNegative = str.startsWith('-');
    if (isNegative && !isNegativeNumAllowed) return false;

    // Check for decimal point allowance
    const hasDecimal = str.includes('.');
    if (hasDecimal && !isFloatAllowed) return false;

    // allow only digits, one leading '-', one '.'
    if (!/^[-]?\d*\.?\d*$/.test(str)) return false;
    if (str.includes('-') && str[0] !== '-') return false;
    if ((str.match(/\./g) || []).length > 1) return false;

    // remove '-' and '.'
    const digitsOnly = str.replace(/[-.]/g, '');

    // no digits at all → invalid
    if (digitsOnly.length === 0) return false;

    // negative side digit limit
    if (isNegative && minDigit < 0) {
      const minLength = Math.abs(minDigit);
      if (digitsOnly.length > minLength) return false;
    }

    // positive side digit limit
    if (!isNegative && maxDigit !== undefined) {
      if (digitsOnly.length > maxDigit) return false;
    }

    return true;
  }

  const OnChangeRange = (index: number) => {
    const updatedOptions = RangeOptions.map((opt, i) => ({
      ...opt,
      selected: i === index,
    }));
    setRangeOptions(updatedOptions);

    // Clear all errors when switching range types
    setErrors({
      rangeValue: '',
      minValue: '',
      maxValue: '',
    });

    // Update isError to false when all errors are cleared
    setFilterRangeDetails({
      ...FilterRangeDetails,
      resultRangeState: 'above', //above / below / between
      aboveValue: '',
      belowValue: '',
      isError: true,
    });
  };

  const handleCheckboxChange = (checked: boolean) => {
    if (!checked) {
      setFilterRangeDetails({
        ...FilterRangeDetails,
        isFilterEnabled: false,
        isError: true,
        ValidationError: false,
        resultRangeState: 'above',
        aboveValue: '',
        belowValue: '',
        options:
          options?.map(opt => ({
            ...opt,
            selected: opt.defaultVal || false,
          })) || [],
      });

      let ClearInitialFilter: any = InitialRangeOptions.map((x, i) => ({
        ...x,
        selected: i === 0,
      }));
      setRangeOptions(ClearInitialFilter);
      setErrors(InitialErrorState);
    } else {
      setRangeOptions(InitialRangeOptions);
      setFilterRangeDetails({
        ...FilterRangeDetails,
        isFilterEnabled: true,
        isError: true,
      });
    }
  };

  const validateBothFields = (
    minVal: string,
    maxVal: string,
    newErrors: typeof errors,
  ) => {
    const isNegativeAttemptMin =
      minDigit >= 0 && /^-\d*\.?\d*$/.test(minVal.trim());
    const isNegativeAttemptMax =
      minDigit >= 0 && /^-\d*\.?\d*$/.test(maxVal.trim());

    if (minVal !== '') {
      if (minVal === '-' || minVal === '.' || !IsValidNumber(minVal)) {
        if (isNegativeAttemptMin) {
          newErrors.minValue = 'Please enter valid value';
        } else if (minVal === '-' || minVal === '.') {
          newErrors.minValue = 'Enter minimum value';
        } else {
          newErrors.minValue = 'Enter minimum value';
        }
      } else if (!twoDecimalRegex.test(minVal)) {
        newErrors.minValue = 'Only up to 2 decimal places allowed';
      } else {
        newErrors.minValue = '';
      }
    } else {
      newErrors.minValue = '';
    }

    if (maxVal !== '') {
      if (maxVal === '-' || maxVal === '.' || !IsValidNumber(maxVal)) {
        if (isNegativeAttemptMax) {
          newErrors.maxValue = 'Please enter valid value';
        } else if (maxVal === '-' || maxVal === '.') {
          newErrors.maxValue = 'Enter maximum value';
        } else {
          newErrors.maxValue = 'Enter maximum value';
        }
      } else if (!twoDecimalRegex.test(maxVal)) {
        newErrors.maxValue = 'Only up to 2 decimal places allowed';
      } else {
        newErrors.maxValue = '';
      }
    } else {
      newErrors.maxValue = '';
    }

    if (
      minVal !== '' &&
      maxVal !== '' &&
      !newErrors.minValue &&
      !newErrors.maxValue
    ) {
      const minNum = Number(minVal);
      const maxNum = Number(maxVal);
      if (minNum >= maxNum) {
        newErrors.minValue = 'Min must be less than max';
        newErrors.maxValue = 'Max must be greater than min';
      }
    }
  };

  // Filter input to only allow numbers, minus signs, and decimal points
  const filterInput = (input: string): string => {
    // Remove any character that isn't a digit, minus sign, or decimal point
    let filtered = input.replace(/[^0-9.-]/g, '');

    // Handle multiple decimal points - keep only the first one
    const decimalIndex = filtered.indexOf('.');
    if (decimalIndex !== -1) {
      const beforeDecimal = filtered.substring(0, decimalIndex + 1);
      const afterDecimal = filtered
        .substring(decimalIndex + 1)
        .replace(/\./g, '');
      filtered = beforeDecimal + afterDecimal;
    }

    // Handle multiple minus signs - keep only the first one at the beginning
    const minusIndex = filtered.indexOf('-');
    if (minusIndex !== -1) {
      if (minusIndex === 0) {
        // Minus sign is at the beginning, remove any other minus signs
        const beforeMinus = filtered.substring(0, 1);
        const afterMinus = filtered.substring(1).replace(/-/g, '');
        filtered = beforeMinus + afterMinus;
      } else {
        // Minus sign is not at the beginning, remove all minus signs
        filtered = filtered.replace(/-/g, '');
      }
    }

    return filtered;
  };

  const OnChangeInputText = (
    selectedRange: string,
    KeyName: string,
    KeyValue: any,
  ) => {
    let stringValue = KeyValue.toString();

    // Apply input filtering to only allow numbers, minus signs, and decimal points
    const filteredValue = filterInput(stringValue);

    // If filtering changed the value, update the TextInput with the filtered value
    if (filteredValue !== stringValue) {
      // Update the TextInput value to the filtered value
      const updatedValue = filteredValue;

      // Set error message for invalid characters
      let newErrors = {...errors};
      if (KeyName === 'aboveValue') {
        if (selectedRange === 'between') {
          newErrors.minValue = 'Please enter numeric values only';
        } else {
          newErrors.rangeValue = 'Please enter numeric values only';
        }
      } else if (KeyName === 'belowValue') {
        if (selectedRange === 'between') {
          newErrors.maxValue = 'Please enter numeric values only';
        } else {
          newErrors.rangeValue = 'Please enter numeric values only';
        }
      }
      setErrors(newErrors);

      // Update state with the filtered value
      if (KeyName === 'aboveValue' && selectedRange !== 'below') {
        setFilterRangeDetails({
          ...FilterRangeDetails,
          resultRangeState: selectedRange,
          aboveValue: updatedValue,
          belowValue: belowValue,
          isError: true,
        });
      } else if (KeyName === 'belowValue' && selectedRange !== 'above') {
        setFilterRangeDetails({
          ...FilterRangeDetails,
          resultRangeState: selectedRange,
          aboveValue: aboveValue,
          belowValue: updatedValue,
          isError: true,
        });
      }
      return;
    }
    const wasInvalidCharError =
      (errors.rangeValue === 'Please enter numeric values only' &&
        KeyName !== 'rangeValue') ||
      (errors.minValue === 'Please enter numeric values only' &&
        KeyName !== 'aboveValue') ||
      (errors.maxValue === 'Please enter numeric values only' &&
        KeyName !== 'belowValue');

    // Initialize newErrors with current errors
    let newErrors = {...errors};
    // Only clear the error for this field if the value is not empty
    if (stringValue === '') {
      newErrors[KeyName as keyof typeof errors] = '';
    } else if (wasInvalidCharError) {
      // Keep the invalid character error until user provides valid input
      // but don't overwrite it with empty string
    } else {
      newErrors[KeyName as keyof typeof errors] = '';
    }
    let IsError;

    // Handle whitespace - trim the input
    if (stringValue !== stringValue.trim()) {
      stringValue = stringValue.trim();
      // If after trimming it becomes empty or invalid, don't update state
      if (stringValue === '' || !IsValidNumber(stringValue)) {
        return;
      }
    }

    // If negative numbers are not allowed and user enters '-', remove it from input
    if (
      !isNegativeNumAllowed &&
      (stringValue === '-' ||
        (stringValue.startsWith('-') && stringValue.length === 1))
    ) {
      stringValue = ''; // Remove the negative sign
      if (isNegativeNumAllowed === false) {
        let newErrors = {...errors};
        if (
          KeyName === 'aboveValue' &&
          (selectedRange === 'above' || selectedRange === 'between')
        ) {
          newErrors.rangeValue = 'Please enter numeric values only';
          if (selectedRange === 'between') {
            newErrors.minValue = 'Please enter numeric values only';
          }
        } else if (
          KeyName === 'belowValue' &&
          (selectedRange === 'below' || selectedRange === 'between')
        ) {
          newErrors.rangeValue = 'Please enter numeric values only';
          if (selectedRange === 'between') {
            newErrors.maxValue = 'Please enter numeric values only';
          }
        }
        setErrors(newErrors);
        setFilterRangeDetails({
          ...FilterRangeDetails,
          resultRangeState: selectedRange,
          isError: true,
        });
        return;
      }
    }

    // Prevent '.' from being entered as the first digit
    if (stringValue === '.') {
      stringValue = ''; // Remove the decimal point

      // Show error message for decimal point at first position
      if (
        KeyName === 'aboveValue' &&
        (selectedRange === 'above' || selectedRange === 'between')
      ) {
        newErrors.rangeValue = 'Please enter valid value';
        if (selectedRange === 'between') {
          newErrors.minValue = 'Please enter valid value';
        }
      } else if (
        KeyName === 'belowValue' &&
        (selectedRange === 'below' || selectedRange === 'between')
      ) {
        newErrors.rangeValue = 'Please enter valid value';
        if (selectedRange === 'between') {
          newErrors.maxValue = 'Please enter valid value';
        }
      }
      setErrors(newErrors);
      return;
    }

    // Prevent '.' from being entered as the second digit when first digit is '-'
    if (stringValue === '-.') {
      stringValue = '-'; // Remove the decimal point, keep the negative sign

      // Show error message for decimal point after negative sign
      if (
        KeyName === 'aboveValue' &&
        (selectedRange === 'above' || selectedRange === 'between')
      ) {
        newErrors.rangeValue = 'Please enter valid value';
        if (selectedRange === 'between') {
          newErrors.minValue = 'Please enter valid value';
        }
      } else if (
        KeyName === 'belowValue' &&
        (selectedRange === 'below' || selectedRange === 'between')
      ) {
        newErrors.rangeValue = 'Please enter valid value';
        if (selectedRange === 'between') {
          newErrors.maxValue = 'Please enter valid value';
        }
      }
      setErrors(newErrors);
      return;
    }

    // If float is not allowed, prevent any decimal point from being entered
    if (!isFloatAllowed && stringValue.includes('.')) {
      // Remove the decimal point from the string
      stringValue = stringValue.replace('.', '');

      // Show error message for decimal point when float is not allowed
      if (
        KeyName === 'aboveValue' &&
        (selectedRange === 'above' || selectedRange === 'between')
      ) {
        newErrors.rangeValue = 'Please enter valid value';
        if (selectedRange === 'between') {
          newErrors.minValue = 'Please enter valid value';
        }
      } else if (
        KeyName === 'belowValue' &&
        (selectedRange === 'below' || selectedRange === 'between')
      ) {
        newErrors.rangeValue = 'Please enter valid value';
        if (selectedRange === 'between') {
          newErrors.maxValue = 'Please enter valid value';
        }
      }
      setErrors(newErrors);
      return;
    }

    // Prevent '-' from being entered anywhere except as the first digit
    if (
      stringValue.length > 1 &&
      stringValue.includes('-') &&
      stringValue[0] !== '-'
    ) {
      // Remove all '-' signs that are not at the beginning
      stringValue = stringValue.replace(/-/g, '');

      // Show error message for invalid negative sign position
      if (
        KeyName === 'aboveValue' &&
        (selectedRange === 'above' || selectedRange === 'between')
      ) {
        newErrors.rangeValue = 'Please enter valid value';
        if (selectedRange === 'between') {
          newErrors.minValue = 'Please enter valid value';
        }
      } else if (
        KeyName === 'belowValue' &&
        (selectedRange === 'below' || selectedRange === 'between')
      ) {
        newErrors.rangeValue = 'Please enter valid value';
        if (selectedRange === 'between') {
          newErrors.maxValue = 'Please enter valid value';
        }
      }
      setErrors(newErrors);
      return;
    }

    if (stringValue !== '' && !IsValidNumber(stringValue)) {
      // If negative numbers are not allowed, show error for negative input
      if (!isNegativeNumAllowed && /^-\d*\.?\d*$/.test(stringValue.trim())) {
        // Show error message for invalid input
        if (
          KeyName === 'aboveValue' &&
          (selectedRange === 'above' || selectedRange === 'between')
        ) {
          newErrors.rangeValue = 'Please enter valid value';
          if (selectedRange === 'between') {
            newErrors.minValue = 'Please enter valid value';
          }
        } else if (
          KeyName === 'belowValue' &&
          (selectedRange === 'below' || selectedRange === 'between')
        ) {
          newErrors.rangeValue = 'Please enter valid value';
          if (selectedRange === 'between') {
            newErrors.maxValue = 'Please enter valid value';
          }
        }
        setErrors(newErrors);
        return;
      }

      // For other invalid inputs, don't update state
      return;
    }

    const numValue = Number(KeyValue) || 0;
    const isNegativeAttempt =
      !isNegativeNumAllowed && /^-\d*\.?\d*$/.test(stringValue.trim());

    if (KeyName === 'aboveValue' && selectedRange === 'above') {
      if (isNegativeAttempt) {
        newErrors.rangeValue = 'Please enter valid value';
        IsError = true;
      } else if (stringValue === '-') {
        !isNegativeNumAllowed
          ? (newErrors.rangeValue = 'Please enter valid value')
          : '';
        IsError = true;
      } else if (stringValue === '.') {
        newErrors.rangeValue = 'Please enter valid value';
        IsError = true;
      } else if (stringValue === '') {
        newErrors.rangeValue = '';
        IsError = true;
      } else {
        newErrors.rangeValue = '';
        IsError = false;
      }
    }
    if (KeyName === 'belowValue' && selectedRange === 'below') {
      if (isNegativeAttempt) {
        newErrors.rangeValue = 'Please enter valid value';
        IsError = true;
      } else if (stringValue === '-') {
        !isNegativeNumAllowed
          ? (newErrors.rangeValue = 'Please enter valid value')
          : '';
        IsError = true;
      } else if (stringValue === '.') {
        newErrors.rangeValue = 'Please enter valid value';
        IsError = true;
      } else if (stringValue === '') {
        newErrors.rangeValue = '';
        IsError = true;
      } else {
        newErrors.rangeValue = '';
        IsError = false;
      }
    }
    //================================================
    else if (KeyName === 'aboveValue' && selectedRange === 'between') {
      // Allow only 2 decimal places
      if (!twoDecimalRegex.test(stringValue)) {
        newErrors.minValue = 'Only up to 2 decimal places allowed';
        IsError = true;
        return;
      }

      if (isNegativeAttempt) {
        newErrors.minValue = 'Please enter valid value';
        IsError = true;
      } else if (stringValue === '-') {
        IsError = true;
      } else if (stringValue === '.') {
        newErrors.minValue = `Enter minimum value`;
        IsError = true;
      } else if (stringValue === '') {
        newErrors.minValue = '';
        IsError = true;
      } else {
        newErrors.minValue = '';
        if (newErrors.maxValue === '') {
          IsError = false;
        }
      }

      // Cross-field validation
      if (
        stringValue !== '' &&
        belowValue !== '' &&
        numValue >= Number(belowValue)
      ) {
        newErrors.minValue = 'Min must be less than max';
        IsError = true;
      }
      validateBothFields(stringValue, belowValue, newErrors);
    }
    //================================================
    else if (KeyName === 'belowValue' && selectedRange === 'between') {
      // Allow only 2 decimal places
      if (!twoDecimalRegex.test(stringValue)) {
        newErrors.maxValue = 'Only up to 2 decimal places allowed';
        IsError = true;
        return;
      }

      if (isNegativeAttempt) {
        newErrors.maxValue = 'Please enter valid value';
        IsError = true;
      } else if (stringValue === '-') {
        IsError = true;
      } else if (stringValue === '.') {
        newErrors.maxValue = 'Enter maximum value';
        IsError = true;
      } else if (stringValue === '') {
        newErrors.maxValue = '';
        IsError = true;
      } else {
        newErrors.maxValue = '';
        if (newErrors.minValue === '') {
          IsError = false;
        }
      }

      // Cross-field validation
      if (
        stringValue !== '' &&
        aboveValue !== '' &&
        numValue <= Number(aboveValue)
      ) {
        newErrors.maxValue = 'Max must be greater than min';
        IsError = true;
      }
      validateBothFields(aboveValue, stringValue, newErrors);
    }

    setErrors(newErrors);
    setFilterRangeDetails({
      ...FilterRangeDetails,
      resultRangeState: selectedRange,
      aboveValue:
        KeyName === 'aboveValue' && selectedRange !== 'below'
          ? stringValue
          : aboveValue,
      belowValue:
        KeyName === 'belowValue' && selectedRange !== 'above'
          ? stringValue
          : belowValue,
      isError: IsError,
    });
  };

  function normalizeDigit(xDigit: number) {
    return xDigit < 0 ? Math.abs(xDigit) + 1 : xDigit;
  }

  return (
    <View
      testID={MyTeamsTestIDs.filterByFQVClientsCoaches}
      accessible={false}
      accessibilityRole="radiogroup"
      accessibilityLabel={Title}
      style={styles.wrapper}>
      <Text
        style={[styles.filterTypeText, styles.filterHeadingText]}
        {...testProperties(Shared.radioButtonGroupTitle)}
        accessible={true}
        accessibilityRole="header">
        {Title}
      </Text>

      {/* Checkbox to enable/disable options */}
      <View
        style={styles.checkboxItem}
        testID={MyTeamsTestIDs.filterByCheckbox}>
        <Checkbox
          testID={`checkbox-filter-${filterByText}`}
          accessible={true}
          accessibilityLabel={`Filter by ${filterByText}: ${
            isFilterEnabled ? 'SELECTED' : ''
          }`}
          checkedIconName={
            isFilterEnabled ? 'checkbox-selected' : 'checkbox-unselected'
          }
          style={{
            icon: [
              styles.checkboxIcon,
              isFilterEnabled && styles.checkboxIconSelected,
            ],
          }}
          rectangleCheckbox
          checked={isFilterEnabled}
          onChange={handleCheckboxChange}
        />
        <Text
          testID={`filter-by-${filterByText
            ?.toLowerCase()
            .replace(/\s+/g, '-')}`}
          style={styles.checkboxLabel}
          isBold>
          Filter by {filterByText}
        </Text>
      </View>

      {/* Radio buttons - disabled when checkbox is unchecked */}
      {isFilterEnabled &&
        options &&
        options.length > 0 &&
        options.map((option: Option, index: number) => (
          <RadioButton
            testID={option.text}
            key={index}
            {...option}
            onPress={() => {
              OnChange(index);
            }}
            isBold={true}
          />
        ))}
      {isFilterEnabled && options && options.length > 0 && (
        <View style={styles.line} />
      )}
      {isFilterEnabled &&
        RangeOptions.map((option: Option, index: number) => (
          <RadioButton
            testID={option.text}
            key={index}
            {...option}
            onPress={() => {
              OnChangeRange(index);
            }}
            isBold={true}
          />
        ))}
      {selectedRangeId === 'above' && isFilterEnabled && (
        <View testID={MyTeamsTestIDs.aboveInputText}>
          <TextInput
            testID={MyTeamsTestIDs.aboveInput}
            maxLength={normalizeDigit(maxDigit)}
            accessible={true}
            accessibilityRole="text"
            accessibilityLabel={`Text input for ${selectedRangeId} value`}
            placeholder="e.g., 5000"
            value={aboveValue}
            onChangeText={(e: any) =>
              OnChangeInputText('above', 'aboveValue', e)
            }
            style={
              errors.rangeValue ? styles.rangeInputError : styles.rangeInput
            }
            keyboardType={keyboardType}
          />
          {errors.rangeValue && (
            <Text
              accessible={true}
              //testID={`${filterByText} ${selectedRangeId} error`}
              style={[styles.errorText, {marginLeft: 10}]}>
              {errors.rangeValue}
            </Text>
          )}
        </View>
      )}
      {selectedRangeId === 'below' && isFilterEnabled && (
        <View testID={MyTeamsTestIDs.belowInputText}>
          <TextInput
            testID={MyTeamsTestIDs.belowInput}
            maxLength={normalizeDigit(maxDigit)}
            placeholder="e.g., 5000"
            value={belowValue}
            onChangeText={(e: any) =>
              OnChangeInputText('below', 'belowValue', e)
            }
            style={
              errors.rangeValue ? styles.rangeInputError : styles.rangeInput
            }
            keyboardType={keyboardType}
            accessible={true}
            accessibilityRole="text"
            accessibilityLabel={`Text input for ${selectedRangeId} value`}
          />
          {errors.rangeValue && (
            <Text
              accessible={true}
              style={[styles.errorText, {marginLeft: 10}]}
              //testID={`${filterByText} ${selectedRangeId} error`}
            >
              {errors.rangeValue}
            </Text>
          )}
        </View>
      )}

      {selectedRangeId === 'between' && isFilterEnabled && (
        <View
          accessible={true}
          style={styles.rangeInputContainer}
          testID={MyTeamsTestIDs.betweenInputMinMaxRange}>
          <View
            testID={MyTeamsTestIDs.betweenInputMin}
            style={styles.rangeFilterGroup}>
            <TextInput
              accessible={true}
              maxLength={normalizeDigit(maxDigit)}
              placeholder="Min"
              value={aboveValue}
              onChangeText={(e: any) =>
                OnChangeInputText('between', 'aboveValue', e)
              }
              style={
                errors.minValue
                  ? styles.rangeInputBetweenError
                  : styles.rangeInputBetween
              }
              keyboardType={keyboardType}
              accessibilityRole="text"
              accessibilityLabel={`Text input for ${selectedRangeId} min value`}
            />
          </View>

          <View
            testID="seperator"
            style={[styles.line, {width: '2%', height: 1, alignSelf: 'center'}]}
          />
          <View
            testID={MyTeamsTestIDs.betweenInputMax}
            style={styles.rangeFilterGroup}>
            <TextInput
              accessible={true}
              maxLength={normalizeDigit(maxDigit)}
              placeholder="Max"
              value={belowValue}
              onChangeText={(e: any) =>
                OnChangeInputText('between', 'belowValue', e)
              }
              style={
                errors.maxValue
                  ? styles.rangeInputBetweenError
                  : styles.rangeInputBetween
              }
              keyboardType={keyboardType}
              accessibilityRole="text"
              accessibilityLabel={`Text input for ${selectedRangeId} max value`}
            />
          </View>
        </View>
      )}

      {selectedRangeId === 'between' && isFilterEnabled && (
        <View style={styles.rangeInputContainer}>
          <View style={styles.inputWithError}>
            {errors.minValue ? (
              <Text
                accessible={true}
                style={styles.errorText}
                //testID={`${filterByText} ${selectedRangeId} min error`}
              >
                {errors.minValue}
              </Text>
            ) : null}
          </View>
          <View style={styles.inputWithError}>
            {errors.maxValue ? (
              <Text
                accessible={true}
                style={styles.errorText}
                //testID={`${filterByText} ${selectedRangeId} max error`}
              >
                {errors.maxValue}
              </Text>
            ) : null}
          </View>
        </View>
      )}
    </View>
  );
};

export default RadioButtonGroup;
